import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { ALLE_NAV_ITEMS } from '@/components/layout/nav-items';

/**
 * Rückweg innerhalb der Anwendung.
 *
 * Warum überhaupt: Wer aus dem Dashboard in eine gefilterte Projektliste
 * springt oder aus der Kundenakte zu den Ansprechpartnern, steht dort ohne
 * Ausgang. Die Seitenleiste führt nur auf die ungefilterten Hauptseiten
 * zurück, nicht dorthin, wo man herkam.
 *
 * Warum an EINER Stelle statt als Zusatz an jedem Link: Die Anwendung hat
 * rund dreißig Routen und deutlich mehr Sprünge dazwischen. Jeder Link müsste
 * seine Herkunft selbst mitgeben, und der nächste neue Link würde es wieder
 * vergessen. Hier merkt sich stattdessen die Hülle, wo der Nutzer war.
 *
 * Warum nicht `navigate(-1)`: Der Verlaufssprung sieht billiger aus, führt
 * aber aus der Anwendung heraus, sobald jemand die Seite direkt aufruft, neu
 * lädt oder aus einem Lesezeichen kommt — und beschriften ließe er sich auch
 * nicht, weil niemand weiß, was einen Schritt zurück liegt.
 */
export interface Rueckweg {
  /** Interner Pfad inklusive Filter-Parametern. */
  to: string;
  /** Beschriftung neben dem Pfeil, z. B. „Dashboard". */
  label: string;
}

/**
 * Beschriftungen für Seiten ohne Eintrag in der Hauptnavigation.
 *
 * Reihenfolge zählt: Das erste passende Muster gewinnt, deshalb steht
 * `/projects/new` vor der Zahlen-Route und `/hilfe/...` vor `/hilfe`.
 */
const ZUSATZ_LABEL: ReadonlyArray<{ muster: RegExp; label: string }> = [
  { muster: /^\/contacts$/, label: 'Ansprechpartner' },
  { muster: /^\/imports\/contacts$/, label: 'Ansprechpartner-Import' },
  { muster: /^\/mail$/, label: 'Posteingang' },
  { muster: /^\/projects\/new$/, label: 'Neues Projekt' },
  { muster: /^\/projects\/\d+\/markup/, label: 'Markup-Editor' },
  { muster: /^\/projects\/\d+$/, label: 'Projekt' },
  { muster: /^\/customers\/\d+$/, label: 'Kunde' },
  { muster: /^\/liegenschaften\/\d+$/, label: 'Objekt' },
  { muster: /^\/hilfe\/schulung/, label: 'Schulung' },
  { muster: /^\/hilfe\/handbuch/, label: 'Handbuch' },
  { muster: /^\/hilfe\/videos$/, label: 'Video-Bibliothek' },
  { muster: /^\/hilfe/, label: 'Hilfe' },
];

/**
 * Übergeordnete Seite je Route — greift, wenn es keinen Verlauf gibt: nach
 * dem Neuladen, beim Direktaufruf und beim Öffnen eines geteilten Links.
 * Dann ist der hierarchische Weg der einzig ehrliche.
 */
const UEBERGEORDNET: ReadonlyArray<{
  muster: RegExp;
  ziel: (treffer: RegExpMatchArray) => string;
}> = [
  { muster: /^\/projects\/(\d+)\/markup/, ziel: (t) => `/projects/${t[1]}` },
  { muster: /^\/projects\/new$/, ziel: () => '/projects' },
  { muster: /^\/projects\/\d+$/, ziel: () => '/projects' },
  { muster: /^\/customers\/\d+$/, ziel: () => '/customers' },
  // Ein Objekt gehört immer zu einem Kunden. Ohne Verlauf ist die Kundenliste
  // der einzig ehrliche Rückweg — welcher Kunde es war, weiß die Route nicht.
  { muster: /^\/liegenschaften\/\d+$/, ziel: () => '/customers' },
  // Ansprechpartner gehören immer zu einem Kunden — siehe nav-items.ts,
  // deshalb haben sie bewusst keinen eigenen Menüpunkt.
  { muster: /^\/contacts$/, ziel: () => '/customers' },
  { muster: /^\/imports\/contacts$/, ziel: () => '/contacts' },
  { muster: /^\/hilfe\/.+/, ziel: () => '/hilfe' },
];

const NAV_LABEL: ReadonlyMap<string, string> = new Map(
  ALLE_NAV_ITEMS.map((item) => [item.to, item.label]),
);

/** Beschriftung einer Seite, oder `null`, wenn wir sie nicht benennen können. */
export function labelFuerPfad(pfad: string): string | null {
  const ausNav = NAV_LABEL.get(pfad);
  if (ausNav) return ausNav;
  for (const { muster, label } of ZUSATZ_LABEL) {
    if (muster.test(pfad)) return label;
  }
  return null;
}

/** Übergeordnete Seite, oder `null`, wenn die Route schon oben steht. */
export function elternFuerPfad(pfad: string): string | null {
  for (const { muster, ziel } of UEBERGEORDNET) {
    const treffer = pfad.match(muster);
    if (treffer) return ziel(treffer);
  }
  return null;
}

/**
 * Ermittelt den Rückweg aus zuletzt besuchter Seite und Hierarchie.
 * Als reine Funktion gehalten, damit die Regeln ohne Router testbar sind.
 */
export function rueckwegAus(aktuellerPfad: string, vorherigeSeite: string | null): Rueckweg | null {
  if (vorherigeSeite) {
    const vorherigerPfad = vorherigeSeite.split('?')[0];
    // Ein Filterwechsel auf derselben Seite ist kein Ortswechsel: „Zurück zu
    // Projekte", während man auf Projekte steht, wäre nur verwirrend.
    if (vorherigerPfad !== aktuellerPfad) {
      return { to: vorherigeSeite, label: labelFuerPfad(vorherigerPfad) ?? 'Zurück' };
    }
  }
  const eltern = elternFuerPfad(aktuellerPfad);
  return eltern ? { to: eltern, label: labelFuerPfad(eltern) ?? 'Zurück' } : null;
}

/** Zuletzt besuchte Seite (Pfad + Parameter) oder `null` zu Sitzungsbeginn. */
export const VorherigeSeite = createContext<string | null>(null);

export function useRueckweg(): Rueckweg | null {
  const vorher = useContext(VorherigeSeite);
  const { pathname } = useLocation();
  return rueckwegAus(pathname, vorher);
}
